import { useState, useEffect, SyntheticEvent } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import axios from 'axios';
import { DateField, StatCard, type DataTableColumn } from '@routes-plan/ui';
import { Office } from '../../models/office';
import { Route } from '../../models/route';
import { formatCurrency } from '../../utils/format/currency';
import ConsolidatedExpensesModal from './modals/ConsolidatedExpensesModal';
import ConsolidatedRefinancingModal from './modals/ConsolidatedRefinancingModal';
import { minYmd, nowOperationalDateString } from '../../utils/operationalDateTime';
import { useT } from '../../i18n';
import { setPageTitle } from '../../utils/pageTitle';
import { OfficeSelect, ReportEmpty, ReportFilters, ReportPage, ReportResults, ReportStats, ReportTable, RouteMultiSelect } from './components/ReportLayout';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface TableRow {
  id: number | string;
  route: string;
  routeId?: string;
  cashBoxId?: number;
  paymentTotal: number;
  loanTotal: number;
  interestTotal: number;
  insuranceChargeTotal: number;
  refinanceValue: number;
  refinanceTotal: number;
  otherIncomeTotal: number;
  otherExpenseTotal: number;
  expenseTotal: number;
  currentlyCashBox: number;
  lastCashBox: number;
  cashBoxValue: number;
}

const ConsolidatedDay: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const t = useT();
  setPageTitle('menu.reports.consolidated-day');
  const current_date = nowOperationalDateString();

  const [office_id, setOffice_id] = useState('');
  const [collectsOffice, setCollectsOffice] = useState<Route[]>([]);
  const [selectedCollects, setSelectedCollects] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [start_date, setStart_date] = useState(current_date);
  const [end_date, setEnd_date] = useState(current_date);
  const [datas, setDatas] = useState<any[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [openModalLoan, setOpenModalLoan] = useState(false);
  const [modalData, setModalData] = useState<{ cashBoxId: number; startDate: string; endDate: string; routeName: string, routeId: string, typeOperation: number; isOutflow?: boolean } | null>(null);
  const [modalLoan, setModalLoan] = useState<{ cashBoxId: number; startDate: string; endDate: string; routeName: string, routeId: string } | null>(null);
  const [queried, setQueried] = useState(false);

  useEffect(() => {
    axios.get('offices').then(({ data }) => setOffices(data.data));
  }, []);

  useEffect(() => {
    if (office_id) {
      axios.get(`offices/${office_id}/routes`).then(({ data }) => {
        setCollectsOffice(data);
        setSelectedCollects([]);
        setSelectAll(false);
      });
    }
  }, [office_id]);

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalData(null);
  };

  const handleOpenModalWithType = (row: any, typeOperation: number) => {
    setModalData({
      cashBoxId: row.cashBoxId,
      startDate: start_date,
      endDate: end_date,
      routeId: row.routeId,
      routeName: row.route,
      typeOperation,
      isOutflow: typeOperation === 1,
    });
    setOpenModal(true);
  };
  const handleCloseModalLoan = () => {
    setOpenModalLoan(false);
    setModalLoan(null);
  };
  const handleOpenModalLoanType = (row: any) => {
    setModalLoan({
      cashBoxId: row.cashBoxId,
      startDate: start_date,
      endDate: end_date,
      routeName: row.route,
      routeId: row.routeId,
    });
    setOpenModalLoan(true);
  };
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!selectedCollects || selectedCollects.length === 0) {
      showAlert(t('summaryReports.selectAtLeastOneRoute'), 'warning');
      return;
    }
    const formData = { endDate: end_date, startDate: start_date, routeId: selectedCollects };
    showLoading();
    setQueried(true);
    try {
      const response = await axios.post('/reports/consolidated-day', formData);
      setDatas(response.data.data);
      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (!response.data.data || response.data.data.length === 0) {
        showAlert(t('summaryReports.noRecordsForSelection'), 'info');
        return;
      }

    } catch (error) {
      console.error('Error:', error);
      showAlert(t('summaryReports.loadDataError'), 'error');
    } finally {
      hideLoading();
    }
  };

  const totalRowId = 'total-row';

  // Filas por ruta y fila de Total al final (mismos cálculos que antes).
  const dataRows: TableRow[] = (datas || []).map((item: any, index: number) => ({
    id: index + 1,
    route: item.routeName,
    routeId: item.routeId,
    cashBoxId: item.cashBoxId,
    paymentTotal: item.totals.paymentTotal || 0,
    loanTotal: item.totals.loanTotal || 0,
    interestTotal: item.totals.interestTotal || 0,
    insuranceChargeTotal: item.totals.insuranceChargeTotal || 0,
    refinanceValue: item.totals.refinanceValue || 0,
    refinanceTotal: item.totals.refinanceTotal || 0,
    otherIncomeTotal: item.totals.otherIncomeTotal || 0,
    otherExpenseTotal: item.totals.otherExpenseTotal || 0,
    expenseTotal: item.totals.expenseTotal || 0,
    currentlyCashBox: item.totals.currentlyCashBox || 0,
    lastCashBox: item.totals.lastCashBox || 0,
    cashBoxValue: item.totals.cashBoxValue || 0,
  }));

  const totals: TableRow = {
    id: totalRowId,
    route: t('summaryReports.total'),
    routeId: undefined,
    cashBoxId: undefined,
    paymentTotal: dataRows.reduce((sum, row) => sum + row.paymentTotal, 0),
    loanTotal: dataRows.reduce((sum, row) => sum + row.loanTotal, 0),
    interestTotal: dataRows.reduce((sum, row) => sum + row.interestTotal, 0),
    insuranceChargeTotal: dataRows.reduce((sum, row) => sum + row.insuranceChargeTotal, 0),
    refinanceValue: dataRows.reduce((sum, row) => sum + row.refinanceValue, 0),
    refinanceTotal: dataRows.reduce((sum, row) => sum + row.refinanceTotal, 0),
    otherIncomeTotal: dataRows.reduce((sum, row) => sum + row.otherIncomeTotal, 0),
    otherExpenseTotal: dataRows.reduce((sum, row) => sum + row.otherExpenseTotal, 0),
    expenseTotal: dataRows.reduce((sum, row) => sum + row.expenseTotal, 0),
    currentlyCashBox: dataRows.reduce((sum, row) => sum + row.currentlyCashBox, 0),
    lastCashBox: dataRows.reduce((sum, row) => sum + row.lastCashBox, 0),
    cashBoxValue: dataRows.reduce((sum, row) => sum + row.cashBoxValue, 0),
  };

  const rows = dataRows.length > 0 ? [...dataRows, totals] : [];
  const isTotal = (row: TableRow) => row.id === totalRowId;

  const moneyColumn = (field: keyof TableRow, header: string, onClick?: (row: TableRow) => void): DataTableColumn<TableRow> => ({
    id: String(field),
    header,
    align: 'right',
    value: (row) => Number(row[field] ?? 0),
    csv: (row) => formatCurrency(Number(row[field] ?? 0)),
    render: (row) => {
      const text = formatCurrency(Number(row[field] ?? 0));
      if (isTotal(row)) return <Box component="strong">{text}</Box>;
      if (!onClick) return text;
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          {text}
          <Tooltip title={t('summaryReports.daily.viewDetail')}>
            <IconButton size="small" color="primary" aria-label={`${t('summaryReports.daily.viewDetail')}: ${header}`} onClick={() => onClick(row)} sx={{ p: 0.25 }}>
              <SearchOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    },
  });

  const columns: DataTableColumn<TableRow>[] = [
    { id: 'route', header: t('summaryReports.shared.route'), value: (row) => row.route, render: (row) => (isTotal(row) ? <Box component="strong">{row.route}</Box> : row.route) },
    moneyColumn('paymentTotal', t('summaryReports.col.payments')),
    moneyColumn('loanTotal', t('summaryReports.col.loans')),
    moneyColumn('otherIncomeTotal', t('summaryReports.col.officeExpensesPlus'), (row) => handleOpenModalWithType(row, 1)),
    moneyColumn('otherExpenseTotal', t('summaryReports.col.officeExpensesMinus'), (row) => handleOpenModalWithType(row, 2)),
    moneyColumn('expenseTotal', t('summaryReports.col.routeCashExpenses'), (row) => handleOpenModalWithType(row, 3)),
    moneyColumn('lastCashBox', t('summaryReports.col.previousCash')),
    moneyColumn('cashBoxValue', t('summaryReports.col.cashTotal')),
  ];

  return (
    <ReportPage title={t('summaryReports.consolidated.title')} description={t('summaryReports.consolidated.description')}>
      <ReportFilters onSubmit={() => submit({ preventDefault: () => undefined } as SyntheticEvent)}>
        <DateField
          label={t('summaryReports.startDate')}
          value={start_date}
          onChange={(v) => setStart_date(v ?? '')}
          max={minYmd(end_date, nowOperationalDateString())}
          size="small"
          fullWidth
        />
        <DateField
          label={t('summaryReports.endDate')}
          value={end_date}
          onChange={(v) => setEnd_date(v ?? '')}
          min={start_date}
          max={nowOperationalDateString()}
          size="small"
          fullWidth
        />
        <OfficeSelect offices={offices} value={office_id} onChange={setOffice_id} />
        <RouteMultiSelect
          routes={collectsOffice}
          value={selectedCollects}
          onChange={(ids) => {
            setSelectedCollects(ids);
            setSelectAll(ids.length > 0 && ids.length === collectsOffice.length);
          }}
        />
      </ReportFilters>

      {dataRows.length > 0 && (
        <ReportStats>
          <StatCard label={t('summaryReports.col.payments')} value={formatCurrency(totals.paymentTotal)} />
          <StatCard label={t('summaryReports.col.loans')} value={formatCurrency(totals.loanTotal)} />
          <StatCard label={t('summaryReports.col.routeCashExpenses')} value={formatCurrency(totals.expenseTotal)} />
          <StatCard label={t('summaryReports.col.cashTotal')} value={formatCurrency(totals.cashBoxValue)} />
        </ReportStats>
      )}

      <ReportResults
        title={dataRows.length > 0 ? t('summaryReports.consolidated.reportTitle') : undefined}
        meta={dataRows.length > 0 ? t('summaryReports.dateRange', { start: start_date, end: end_date }) : undefined}
      >
        <ReportTable<TableRow>
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          searchable={false}
          exportFileName={t('summaryReports.consolidated.exportFile')}
          ariaLabel={t('summaryReports.consolidated.title')}
          emptyState={<ReportEmpty queried={queried} />}
        />
      </ReportResults>

      <ConsolidatedExpensesModal
        open={openModal}
        onClose={handleCloseModal}
        clientData={modalData}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
      <ConsolidatedRefinancingModal
        open={openModalLoan}
        onClose={handleCloseModalLoan}
        clientLoan={modalLoan}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
    </ReportPage>
  );
};

export default ConsolidatedDay;
